/**
 * Hand-written wrapper for the Calculator v3 workloading endpoints.
 *
 * These endpoints are not yet in the generated OpenAPI client because the
 * backend changes haven't landed in the dev environment that
 * `npm run api-generate` reads from. Once they do, swap the internals below
 * to use `new WorkloadingApi()` — the exported function signatures stay
 * stable.
 */
import axiosInstance from './axios';

// ── Types (kept loose so generated client migration is trivial) ──────────────

export interface AreaInput {
  cleanableSqft: number;
  density?: string;
  fixtureCount?: number;
  rentableSqft?: number;
  soilLevel?: string;
}

export interface TaskInput {
  chemicalCostPerUnit?: number;
  code: string;
  fixedMaterialCostPerEmployee?: number;
  measure?: string;
  measurePerHour: number;
  name?: string;
  quantity: number;
  vanTripCost?: number;
}

export interface FrequencyInput {
  frequencyDaysCount: number;
  operatingWeeks: number;
  visitsPerWeek: number;
}

export interface ServiceDefInput {
  setupHours: number;
  tasks: TaskInput[];
  teardownHours: number;
  travelHours: number;
}

export interface WorkloadRequest {
  areas: AreaInput[];
  completionWindowHours: number;
  defaultServiceCode?: string;
  frequency: FrequencyInput;
  medianLaborRate: number;
  service: ServiceDefInput;
  // Per-lead supervisor inputs. When supplied, override the company-wide
  // SVR/SVH static-cost rows. Pass 0 to mean "no supervision on this lead."
  supervisorHoursPerYear?: number;
  supervisorRate?: number;
  targetMargin: number;
  totalCleanableSqftOverride?: number;
}

export interface CostBreakdown {
  directLaborCost: number;
  equipmentCost: number;
  materialCost: number;
  overheadCost: number;
  payrollCost: number;
  supervisionCost: number;
  suppliesCost: number;
  totalCost: number;
  uniformsCost: number;
  vanTripCost: number;
}

export interface DerivedMetrics {
  costPerSqftPerMonth: number;
  costPerSqftPerVisit: number;
  costPerSqftPerYear: number;
  effectiveHourlyRate: number;
}

export interface WorkloadingOutput {
  annualPrice: number;
  calculatedAt: string;
  configGaps: string[];
  cost: CostBreakdown;
  crewSize: number;
  derived: DerivedMetrics;
  hoursPerVisit: number;
  hoursPerYear: number;
  monthlyPrice: number;
  totalCleanableSqft: number;
  totalVisits: number;
}

export interface LeadServiceTimeWithMode {
  completionWindowHours?: number;
  hoursOverrideActive: boolean;
  id: number;
  workloadingOutputJson?: WorkloadingOutput;
}

// ── Calls ───────────────────────────────────────────────────────────────────

const preview = async (
  request: WorkloadRequest,
): Promise<WorkloadingOutput> => {
  const { data } = await axiosInstance.post<WorkloadingOutput>(
    '/workloading/preview',
    request,
  );
  return data;
};

const commitToServiceTime = async (
  serviceTimeId: number,
  output: WorkloadingOutput,
): Promise<LeadServiceTimeWithMode> => {
  const { data } = await axiosInstance.post<LeadServiceTimeWithMode>(
    `/workloading/lead-service-time/${serviceTimeId}/commit`,
    output,
  );
  return data;
};

const setOverrideMode = async (
  serviceTimeId: number,
): Promise<LeadServiceTimeWithMode> => {
  const { data } = await axiosInstance.post<LeadServiceTimeWithMode>(
    `/workloading/lead-service-time/${serviceTimeId}/override`,
    {},
  );
  return data;
};

export interface EffectiveServiceRate {
  effectiveRateSqftPerHour: number;
  globalDefaultRateSqftPerHour: number;
  hasTenantOverride: boolean;
  notes?: string;
  serviceCode: string;
  serviceName: string;
}

const listTenantRates = async (): Promise<EffectiveServiceRate[]> => {
  const { data } = await axiosInstance.get<EffectiveServiceRate[]>(
    '/workloading/tenant-rates',
  );
  return data;
};

const setTenantRate = async (
  serviceCode: string,
  blendedRateSqftPerHour: number | null,
  notes?: string,
): Promise<EffectiveServiceRate[]> => {
  const { data } = await axiosInstance.put<EffectiveServiceRate[]>(
    `/workloading/tenant-rates/${encodeURIComponent(serviceCode)}`,
    { blendedRateSqftPerHour, notes },
  );
  return data;
};

export interface MediaRateChange {
  newRate: number;
  oldRate: number;
  skillIds: string[];
  taskId: number;
  taskName: string;
}

export interface TechnicianRateChange {
  newRate: number;
  oldRate: number;
  taskId: number;
  taskName: string;
  technicianId: string;
  technicianName: string;
}

export interface RecomputeRatesResult {
  leadId: string;
  leadStatus: string;
  mediaRateChanges: MediaRateChange[];
  tasksInspected: number;
  technicianRateChanges: TechnicianRateChange[];
}

const recomputeLeadRates = async (
  leadId: string,
): Promise<RecomputeRatesResult> => {
  const { data } = await axiosInstance.post<RecomputeRatesResult>(
    `/workloading/lead/${encodeURIComponent(leadId)}/recompute-rates`,
    {},
  );
  return data;
};

const workloadingApi = {
  commitToServiceTime,
  listTenantRates,
  preview,
  recomputeLeadRates,
  setOverrideMode,
  setTenantRate,
};

export default workloadingApi;
